import { translate } from "../../../../common/providers";

export const AI_PROVIDER_TYPES = [
	"AZURE_OPENAI",
	"OPENAI_COMPATIBLE",
	"ANTHROPIC",
	"COHERE",
	"MISTRAL_AI",
	"OLLAMA",
	"VERTEX_AI_GEMINI",
	"XAI",
	"BEDROCK_ANTHROPIC",
	"BEDROCK_COHERE",
	"BEDROCK_CONVERSE",
	"BEDROCK_LLAMA",
	"BEDROCK_TITAN",
];

const BEDROCK_PROVIDER_TYPES = [
	"BEDROCK_ANTHROPIC",
	"BEDROCK_COHERE",
	"BEDROCK_CONVERSE",
	"BEDROCK_LLAMA",
	"BEDROCK_TITAN",
];

export const highlightText = (text) => {
	const regex = /\{\{(.*?)\}\}/g;
	return text.replace(regex, (_, p1) => `<span style="color: var(--blue-500);">{{${p1}}}</span>`);
};

export const translateEnumExecutionStatus = (status) =>
	(status && translate(`dsi.agents.execution-history.status.${status.toLowerCase().replace(/_/g, "-")}`)) || "—";

export const getExecutionCost = (execution = {}) => {
	const totalCost = execution?.costTotal;
	if (totalCost != null && Number.isFinite(Number(totalCost))) {
		return Number(totalCost);
	}
	return Number(execution?.costInput || 0) + Number(execution?.costOutput || 0);
};

// Shared USD formatter for the money values shown across the agents views.
const formatUsd = (value) => {
	const numericValue = Number(value);

	if (!Number.isFinite(numericValue)) {
		return "—";
	}

	return `$${numericValue.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 6,
	})}`;
};

export const formatExecutionCost = (value) => formatUsd(value);

export const groupAgentExecutionRows = (executions = []) => {
	const loadedExecutions = Array.isArray(executions) ? executions : [];
	const executionIds = new Map(loadedExecutions.map((execution) => [execution?.id, execution]));
	const groupedChildrenByParentId = new Map();

	loadedExecutions.forEach((execution) => {
		const parentExecutionId = execution?.parentExecutionId;
		if (!parentExecutionId || !executionIds.has(parentExecutionId)) {
			return;
		}
		if (!groupedChildrenByParentId.has(parentExecutionId)) {
			groupedChildrenByParentId.set(parentExecutionId, []);
		}
		groupedChildrenByParentId.get(parentExecutionId).push({
			...execution,
			__isGroupedChild: true,
			__groupKey: parentExecutionId,
		});
	});

	return loadedExecutions.flatMap((execution) => {
		if (execution?.parentExecutionId) {
			return [];
		}
		const groupedChildren = groupedChildrenByParentId.get(execution?.id) || [];
		if (groupedChildren.length === 0) {
			return [execution];
		}
		const groupedChildrenCost = groupedChildren.reduce((sum, child) => sum + getExecutionCost(child), 0);
		return [
			{
				...execution,
				__groupKey: execution.id,
				__hasGroupedChildren: true,
				__groupedChildrenCount: groupedChildren.length,
				__totalGroupedCost: getExecutionCost(execution) + groupedChildrenCost,
			},
			...groupedChildren,
		];
	});
};

export const translateEnumProvider = (value) =>
	(value && translate(`dsi.agents.models.provider.${value.toLowerCase().replace(/_/g, "-")}`)) || "—";

export const getAgentModelDetails = (agent = {}) => {
	const { model = null, usesDefaultModel = false } = agent;
	const isDefaultModel = usesDefaultModel;
	const isLlmNotFound = !usesDefaultModel && model === null;

	return {
		displayName: model?.modelName || model?.deploymentName || null,
		deploymentName: model?.deploymentName || "—",
		modelName: model?.modelName || "—",
		providerName: translateEnumProvider(model?.providerType),
		region: model?.region || "—",
		projectId: model?.projectId || "—",
		location: model?.location || "—",
		isDefaultModel,
		isLlmNotFound,
		isInactive: model?.active === false,
	};
};

export const getModelSelectorItems = (models = []) => [
	{
		id: null,
		name: translate("dsi.agents.dialog.ai-model.default-option"),
		providerType: null,
		active: true,
	},
	...(Array.isArray(models) ? models : [])
		.filter(({ active }) => active !== false)
		.map((model) => ({
			...model,
			label: model?.providerType ? `${model?.name} — ${translateEnumProvider(model.providerType)}` : model?.name,
		})),
];

// The api client interceptor rejects with the formatted error message (a string),
// not the axios error object — accept both shapes.
export const mapAgentModelError = (error) => {
	const message = (typeof error === "string" ? error : error?.response?.data?.message || error?.message) || "";
	const normalized = message.toLowerCase();

	if (normalized.includes("inactive")) {
		return translate("dsi.agents.errors.inactive-model");
	}
	if (normalized.includes("default") && normalized.includes("not found")) {
		return translate("dsi.agents.errors.default-model-not-found");
	}
	if (
		normalized.includes("incomplete") ||
		normalized.includes("invalid runtime") ||
		normalized.includes("configuration is incomplete")
	) {
		return translate("dsi.agents.errors.invalid-runtime-configuration");
	}
	if (normalized.includes("used by at least one agent") || normalized.includes("cannot be deleted")) {
		return translate("dsi.agents.errors.model-in-use");
	}
	if (normalized.includes("access key") && normalized.includes("secret key")) {
		return translate("dsi.agents.errors.bedrock-credentials-pair");
	}

	return message;
};

// Returns the currency-formatted per-million-tokens price. The "/ 1M" unit is carried
// by the column header (dsi.agents.ai-models.*-cost.column), so it is not repeated here.
export const formatTokenPrice = (value) => formatUsd(value);

export const getProviderFieldConfig = (providerType) => {
	const isBedrock = BEDROCK_PROVIDER_TYPES.includes(providerType);
	const commonFields = {
		baseUrl: false,
		apiKey: false,
		deploymentName: false,
		modelName: false,
		region: false,
		projectId: false,
		location: false,
		accessKey: false,
		secretKey: false,
	};

	if (providerType === "AZURE_OPENAI") {
		return {
			...commonFields,
			baseUrl: true,
			apiKey: true,
			deploymentName: true,
			modelName: true,
		};
	}
	if (providerType === "OPENAI_COMPATIBLE") {
		return {
			...commonFields,
			baseUrl: true,
			apiKey: true,
			modelName: true,
		};
	}
	if (["ANTHROPIC", "COHERE", "MISTRAL_AI", "XAI"].includes(providerType)) {
		return {
			...commonFields,
			baseUrl: true,
			apiKey: true,
			modelName: true,
		};
	}
	if (providerType === "OLLAMA") {
		return {
			...commonFields,
			baseUrl: true,
			modelName: true,
		};
	}
	if (providerType === "VERTEX_AI_GEMINI") {
		return {
			...commonFields,
			modelName: true,
			projectId: true,
			location: true,
		};
	}
	if (isBedrock) {
		return {
			...commonFields,
			modelName: true,
			region: true,
			accessKey: true,
			secretKey: true,
		};
	}

	return commonFields;
};

export const validateModelForm = ({
	providerType,
	baseUrl,
	apiKey,
	deploymentName,
	modelName,
	region,
	projectId,
	location,
	accessKey,
	secretKey,
}) => {
	const errors = {};
	const config = getProviderFieldConfig(providerType);
	const isBlank = (value) => `${value || ""}`.trim().length === 0;

	if (isBlank(providerType)) {
		errors.providerType = translate("dsi.agents.ai-models.validation.required");
	}
	if (providerType === "AZURE_OPENAI") {
		if (isBlank(baseUrl)) {
			errors.baseUrl = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(apiKey)) {
			errors.apiKey = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(deploymentName)) {
			errors.deploymentName = translate("dsi.agents.ai-models.validation.required");
		}
	}
	if (providerType === "OPENAI_COMPATIBLE") {
		if (isBlank(baseUrl)) {
			errors.baseUrl = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(apiKey)) {
			errors.apiKey = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(modelName)) {
			errors.modelName = translate("dsi.agents.ai-models.validation.required");
		}
	}
	if (["ANTHROPIC", "COHERE", "MISTRAL_AI", "XAI"].includes(providerType)) {
		if (isBlank(apiKey)) {
			errors.apiKey = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(modelName)) {
			errors.modelName = translate("dsi.agents.ai-models.validation.required");
		}
	}
	if (providerType === "OLLAMA") {
		if (isBlank(baseUrl)) {
			errors.baseUrl = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(modelName)) {
			errors.modelName = translate("dsi.agents.ai-models.validation.required");
		}
	}
	if (providerType === "VERTEX_AI_GEMINI") {
		if (isBlank(projectId)) {
			errors.projectId = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(location)) {
			errors.location = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(modelName)) {
			errors.modelName = translate("dsi.agents.ai-models.validation.required");
		}
	}
	if (BEDROCK_PROVIDER_TYPES.includes(providerType)) {
		if (isBlank(region)) {
			errors.region = translate("dsi.agents.ai-models.validation.required");
		}
		if (isBlank(modelName)) {
			errors.modelName = translate("dsi.agents.ai-models.validation.required");
		}
		if ((isBlank(accessKey) && !isBlank(secretKey)) || (!isBlank(accessKey) && isBlank(secretKey))) {
			errors.accessKey = translate("dsi.agents.ai-models.validation.credentials-pair");
			errors.secretKey = translate("dsi.agents.ai-models.validation.credentials-pair");
		}
	}

	Object.keys(config).forEach((field) => {
		if (!config[field]) {
			delete errors[field];
		}
	});

	return errors;
};

export const getModelFormPayload = ({
	name,
	description,
	providerType,
	baseUrl,
	deploymentName,
	modelName,
	apiKey,
	region,
	projectId,
	location,
	accessKey,
	secretKey,
	providerOptions,
	active,
	inputCostPerMillion,
	outputCostPerMillion,
}) => ({
	name,
	description: description || null,
	providerType,
	baseUrl: baseUrl || null,
	deploymentName: deploymentName || null,
	modelName: modelName || null,
	apiKey: apiKey || undefined,
	region: region || null,
	projectId: projectId || null,
	location: location || null,
	accessKey: accessKey || undefined,
	secretKey: secretKey || undefined,
	providerOptions: providerOptions || null,
	active,
	inputCostPerMillion: Number(inputCostPerMillion),
	outputCostPerMillion: Number(outputCostPerMillion),
});

export const MODEL_IMPORT_ERROR = {
	NO_FILE: "no-file",
	INVALID_JSON: "invalid-json",
	NOT_ARRAY: "not-array",
};

// Parses the raw text content of an import file and validates that the root payload is an array.
// Throws an Error whose `code` matches a MODEL_IMPORT_ERROR value so the caller can map it to copy.
export const parseModelsImportPayload = (text) => {
	let parsed;
	try {
		parsed = JSON.parse(text);
	} catch (error) {
		const parseError = new Error(MODEL_IMPORT_ERROR.INVALID_JSON);
		parseError.code = MODEL_IMPORT_ERROR.INVALID_JSON;
		throw parseError;
	}
	if (!Array.isArray(parsed)) {
		const shapeError = new Error(MODEL_IMPORT_ERROR.NOT_ARRAY);
		shapeError.code = MODEL_IMPORT_ERROR.NOT_ARRAY;
		throw shapeError;
	}
	return parsed;
};

// Resolves the filename to use for an export download, decoding the backend-provided
// Content-Disposition value when present and falling back to a default otherwise.
export const resolveExportFilename = (filename, fallback) => {
	if (!filename) {
		return fallback;
	}
	try {
		return decodeURIComponent(filename);
	} catch (error) {
		return filename;
	}
};
